import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Icon } from '../components/ui';
import { usePageMeta } from '../lib/seo';
import { formatWhatsapp, useSiteContact, whatsappHref } from '../lib/site';
import { tr } from '../lib/i18n';

function Page({ title, description, path, children }: { title: string; description: string; path: string; children: ReactNode }): JSX.Element {
  usePageMeta(title, description, path);
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-12">
      <h1 className="font-display text-3xl font-semibold text-ink sm:text-4xl">{title}</h1>
      <div className="mt-6 space-y-6 text-[0.975rem] leading-relaxed text-ink">{children}</div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }): JSX.Element {
  return (
    <section>
      <h2 className="font-display text-xl font-semibold text-ink">{heading}</h2>
      <div className="mt-2 space-y-2 text-muted">{children}</div>
    </section>
  );
}

/* ---------------- About ---------------- */

export function AboutPage(): JSX.Element {
  return (
    <Page
      title={tr("Sobre a AgendaPro")}
      description={tr("A AgendaPro é a plataforma de agendamento para profissionais e estabelecimentos de beleza, saúde, bem-estar e serviços no Brasil.")}
      path="/sobre"
    >
      <Section heading={tr("O que fazemos")}>
        <p>
          {tr("A AgendaPro é um software de agendamento (SaaS) feito para salões, barbearias, estúdios de tatuagem e piercing, clínicas de estética e odontologia, fisioterapia, massoterapia, podologia, personal trainers, banho e tosa e aulas ou consultorias. Ajudamos você a organizar a agenda, publicar um perfil profissional com seus serviços e preços, e receber reservas pela internet sem precisar de cadastro do cliente.")}
        </p>
        <p>
          {tr("Nossa proposta é simples:")} <em>{tr("seu negócio. sua agenda. seu tempo.")}</em> {tr("Você configura o expediente, os serviços e as políticas uma vez; o restante — cálculo de horários livres, prevenção de conflitos e atalhos de contato — a plataforma cuida para você.")}
        </p>
      </Section>

      <Section heading={tr("Como o produto funciona")}>
        <p>
          {tr("Cada profissional ou estabelecimento cria uma conta e configura seu perfil. A AgendaPro calcula os horários disponíveis a partir do expediente, das folgas, dos períodos bloqueados e das reservas já existentes. O cliente acessa seu link público, escolhe serviço, data e horário e confirma. Você recebe tudo na agenda.")}
        </p>
      </Section>

      <Section heading={tr("Como ganhamos dinheiro")}>
        <p>
          {tr("Cobramos apenas a assinatura mensal, semestral ou anual do plano escolhido. Não cobramos taxa por agendamento e não vendemos os dados dos seus clientes. Você começa com um teste gratuito de 7 dias e, para continuar depois disso, precisa contratar um plano pago.")}
        </p>
      </Section>

      <Section heading={tr("Nosso compromisso")}>
        <p>
          {tr("Transparência: mostramos exatamente o que a API retorna — sem inventar métricas de \"clientes atendidos\" ou depoimentos fictícios. Se algo ainda não está disponível, avisamos com clareza.")}
        </p>
      </Section>

      <div className="flex gap-3 pt-2">
        <Link to="/precos"><Button>{tr("Ver planos")}</Button></Link>
        <Link to="/contato"><Button variant="secondary">{tr("Falar com a gente")}</Button></Link>
      </div>
    </Page>
  );
}

/* ---------------- Contact ---------------- */

export function ContactPage(): JSX.Element {
  const { supportEmail, supportWhatsapp } = useSiteContact();
  const waHref = whatsappHref(supportWhatsapp);
  return (
    <Page
      title={tr("Contato")}
      description={tr("Canais oficiais para falar com a equipe da AgendaPro.")}
      path="/contato"
    >
      <p>
        {tr("A AgendaPro ainda não disponibiliza um formulário de contato que envia mensagens pelo site. Para não fingir um envio que não existe, oferecemos abaixo os canais que realmente funcionam.")}
      </p>

      <Card as="section">
        <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
          <Icon name="info" /> {tr("E-mail de suporte")}
        </h2>
        <p className="mt-2 text-muted">
          {tr("Um dos canais oficiais para falar com a equipe. O botão abaixo abre o seu aplicativo de e-mail com o assunto preenchido — nenhuma mensagem é enviada automaticamente pela AgendaPro.")}
        </p>
        <div className="mt-4">
          <a href={`mailto:${supportEmail}?subject=${encodeURIComponent('Contato pelo site AgendaPro')}`}>
            <Button icon={<Icon name="user" />}>{supportEmail}</Button>
          </a>
        </div>
      </Card>

      {waHref ? (
        <Card as="section">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
            <Icon name="whatsapp" /> {tr("WhatsApp")}
          </h2>
          <p className="mt-2 text-muted">
            {tr("Atendimento pelo WhatsApp no número")} {formatWhatsapp(supportWhatsapp)}{tr(". O botão abre a conversa no seu aplicativo — a AgendaPro não envia mensagens automáticas.")}
          </p>
          <div className="mt-4">
            <a href={waHref} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" icon={<Icon name="whatsapp" />}>{tr("Abrir conversa no WhatsApp")}</Button>
            </a>
          </div>
        </Card>
      ) : null}

      <Section heading={tr("Já é cliente?")}>
        <p>
          {tr("Se você tem uma conta, o caminho mais rápido é pelo painel em")} <Link to="/painel/suporte" className="underline">{tr("Painel › Suporte")}</Link>{tr(", onde podemos identificar sua conta e responder mais rápido.")}
        </p>
      </Section>

      <Section heading={tr("Profissionais e estabelecimentos")}>
        <p>
          {tr("Dúvidas sobre planos, migração de agenda ou recursos: escreva para")} {supportEmail} {tr("informando o nome do seu estabelecimento. Respondemos em dias úteis.")}
        </p>
      </Section>
    </Page>
  );
}

/* ---------------- FAQ ---------------- */

interface FaqItem { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: 'O que é a AgendaPro?',
    a: 'É uma plataforma de agendamento online (SaaS) para profissionais e estabelecimentos de beleza, saúde, bem-estar e serviços no Brasil. Você configura sua agenda, publica seu perfil e recebe reservas pelo link público.'
  },
  {
    q: 'Como funciona o teste de 7 dias?',
    a: tr("Toda conta começa com um teste gratuito de 7 dias, sem precisar de cartão. Durante o teste você usa o plano à escolha normalmente. Ao final do período, é necessária uma assinatura paga para continuar — sem cobrança automática surpresa no cartão.")
  },
  {
    q: 'Meus clientes precisam criar conta para agendar?',
    a: 'Não. O agendamento pelo seu link público não exige login. O cliente informa nome e um contato (e-mail ou telefone) e escolhe o horário.'
  },
  {
    q: 'Vocês cobram taxa por agendamento?',
    a: 'Não. Cobramos apenas a assinatura do plano escolhido. Não há comissão por reserva.'
  },
  {
    q: 'Posso cadastrar vários profissionais?',
    a: 'Depende do plano. Cada plano tem um limite de profissionais; você pode fazer upgrade quando a equipe crescer.'
  },
  {
    q: 'O que acontece se dois clientes escolherem o mesmo horário?',
    a: 'O sistema bloqueia conflitos: se um horário já foi tomado, a reserva do outro é recusada e o cliente escolhe outro horário disponível.'
  },
  {
    q: 'Como o cliente cancela?',
    a: 'Pelas condições definidas pelo profissional na política do perfil, ou diretamente com o estabelecimento. Você também pode ler nossa política de cancelamento.'
  },
  {
    q: 'Posso migrar minha agenda atual?',
    a: 'Você recria serviços, horários e políticas no painel. O suporte ajuda no processo pelo e-mail de contato.'
  },
  {
    q: 'A AgendaPro envia mensagens automáticas no WhatsApp?',
    a: 'Não. Os botões de WhatsApp abrem a conversa no aplicativo do usuário com um texto pronto para ele enviar; a plataforma não dispara mensagens sozinha.'
  },
  {
    q: 'Quais dados vocês coletam?',
    a: 'Detalhamos na Política de Privacidade. Resumindo: dados de cadastro, agendamentos e cookies de sessão — nada de rastreadores de terceiros sem necessidade.'
  }
];

export function FaqPage(): JSX.Element {
  return (
    <Page title={tr("Dúvidas frequentes")} description={tr("Perguntas e respostas sobre planos, agendamento, teste gratuito e privacidade na AgendaPro.")} path="/faq">
      <div className="space-y-3">
        {FAQ.map((item) => (
          <details key={item.q} className="group rounded-md border border-line bg-card">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 font-medium text-ink">
              {tr(item.q)}
              <span className="text-muted transition-transform group-open:rotate-90" aria-hidden="true"><Icon name="chevron" /></span>
            </summary>
            <div className="border-t border-line px-4 py-3 text-sm text-muted">{tr(item.a)}</div>
          </details>
        ))}
      </div>
      <p className="pt-2 text-sm text-muted">
        {tr("Não encontrou sua dúvida?")} <Link to="/contato" className="underline">{tr("Fale com a gente")}</Link>.
      </p>
    </Page>
  );
}

/* ---------------- Support ---------------- */

export function SupportPage(): JSX.Element {
  const { supportEmail, supportWhatsapp } = useSiteContact();
  const waHref = whatsappHref(supportWhatsapp);
  return (
    <Page title={tr("Suporte")} description={tr("Central de ajuda e canais de suporte da AgendaPro.")} path="/suporte">
      <Section heading={tr("Autoatendimento")}>
        <ul className="list-disc space-y-1 pl-5">
          <li>{tr("Configurar agenda, serviços e horários: veja")} <Link to="/faq" className="underline">{tr("Dúvidas frequentes")}</Link>.</li>
          <li>{tr("Entender planos e cobrança:")} <Link to="/precos" className="underline">{tr("Planos e preços")}</Link>.</li>
          <li>{tr("Cancelar ou alterar reservas:")} <Link to="/cancelamento" className="underline">{tr("Política de cancelamento")}</Link>.</li>
        </ul>
      </Section>

      <Card as="section">
        <h2 className="font-display text-xl font-semibold text-ink">{tr("Abrir chamado por e-mail")}</h2>
        <p className="mt-2 text-muted">
          {tr("Nossa central de suporte funciona por e-mail. Não há um formulário que envia tickets pelo site, então o botão abaixo abre seu aplicativo de e-mail com o destinatário preenchido — nada é enviado automaticamente.")}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={`mailto:${supportEmail}?subject=${encodeURIComponent('Suporte AgendaPro')}`}>
            <Button icon={<Icon name="shield" />}>{tr("Enviar e-mail para")} {supportEmail}</Button>
          </a>
          {waHref ? (
            <a href={waHref} target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" icon={<Icon name="whatsapp" />}>{tr("WhatsApp")} {formatWhatsapp(supportWhatsapp)}</Button>
            </a>
          ) : null}
        </div>
      </Card>

      <Alert tone="info" title={tr("Ao contatar, informe")}>
        {tr("O nome do estabelecimento, o código do agendamento (se houver) e o que aconteceu. Isso agiliza a resposta.")}
      </Alert>
    </Page>
  );
}

/* ---------------- Legal pages ---------------- */

export function TermsPage(): JSX.Element {
  const { supportEmail } = useSiteContact();
  return (
    <Page title={tr("Termos de uso")} description={tr("Termos que regem o uso da plataforma AgendaPro.")} path="/termos">
      <p className="text-sm text-muted">{tr('Última atualização: outubro de 2026.')}</p>

      <Section heading={tr("1. O serviço")}>
        <p>
          {tr("A AgendaPro oferece uma plataforma de agendamento (SaaS) para profissionais e estabelecimentos de beleza, saúde, bem-estar e serviços. O serviço inclui agenda, perfil público, cálculo de disponibilidade e recebimento de reservas. Não somos donos do estabelecimento nem prestamos o serviço agendado: nossa função é a tecnologia de agendamento.")}
        </p>
      </Section>

      <Section heading={tr("2. Cadastro e responsabilidade")}>
        <p>
          {tr("Você é responsável pelas informações cadastradas (serviços, preços, horários e políticas) e por honrar os agendamentos confirmados. Ao criar uma conta, você declara ter capacidade para usar a plataforma e concorda com estes termos.")}
        </p>
      </Section>

      <Section heading={tr("3. Teste gratuito")}>
        <p>
          {tr("Oferecemos um período de teste gratuito de 7 dias, sem cobrança automática e sem exigir cartão no início. Ao final do teste, o uso contínuo depende da contratação de um plano pago. Contas que não contratam um plano podem ter o acesso ao agendamento suspenso até a regularização.")}
        </p>
      </Section>

      <Section heading={tr("4. Planos e cobrança")}>
        <p>
          {tr("Os preços, limites de profissionais e recursos de cada plano são apresentados na página de")}{' '}
          <Link to="/precos" className="underline">{tr("Planos")}</Link>{tr(". A cobrança recorrente depende da configuração do provedor de pagamentos; quando o checkout online ainda não estiver disponível neste ambiente, avisamos com clareza antes de qualquer contratação.")}
        </p>
      </Section>

      <Section heading={tr("5. Boas práticas")}>
        <p>
          {tr("É proibido usar a plataforma para fins ilegais, para publicar conteúdo enganoso ou para tentar sobrecarregar ou comprometer a segurança do serviço. Podemos suspender contas que violem estes termos.")}
        </p>
      </Section>

      <Section heading={tr("6. Limitação de responsabilidade")}>
        <p>
          {tr("A AgendaPro não se responsabiliza por faltas, atrasos ou desentendimentos entre clientes e profissionais, nem por danos decorrentes da prestação do serviço agendado, que é responsabilidade exclusiva do estabelecimento.")}
        </p>
      </Section>

      <Section heading={tr("7. Alterações")}>
        <p>{tr("Estes termos podem ser atualizados. Alterações relevantes serão comunicadas por aqui e, quando aplicável, por e-mail.")}</p>
      </Section>

      <Section heading={tr("8. Contato")}>
        <p>{tr("Dúvidas sobre estes termos:")} {supportEmail}.</p>
      </Section>
    </Page>
  );
}

export function PrivacyPage(): JSX.Element {
  const { supportEmail } = useSiteContact();
  return (
    <Page title={tr("Política de privacidade")} description={tr("Como a AgendaPro trata seus dados pessoais, em conformidade com a LGPD.")} path="/privacidade">
      <p className="text-sm text-muted">{tr('Última atualização: outubro de 2026.')}</p>

      <Section heading={tr("1. Quem somos")}>
        <p>
          {tr("A AgendaPro é a controladora dos dados coletados nesta plataforma. Tratamos dados pessoais conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).")}
        </p>
      </Section>

      <Section heading={tr("2. Dados que coletamos")}>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>{tr("Do profissional/estabelecimento:")}</strong> {tr("nome, e-mail, telefone, dados do negócio, serviços, preços, horários, equipe e agendamentos.")}</li>
          <li><strong>{tr("Do cliente que agenda:")}</strong> {tr("nome, e-mail e/ou telefone, observações e o histórico de reservas daquele estabelecimento.")}</li>
          <li><strong>{tr("De uso e segurança:")}</strong> {tr("registros de sessão, eventos de assinatura e pagamentos (via provedor) e logs necessários ao funcionamento.")}</li>
        </ul>
      </Section>

      <Section heading={tr("3. Finalidades e bases legais")}>
        <p>
          {tr("Usamos os dados para prestar o serviço contratado, autenticar acesso, evitar conflitos de agenda, cumprir obrigações legais e manter a segurança. A base legal é a execução de contrato e o legítimo interesse, sempre que aplicável, e o consentimento quando necessário.")}
        </p>
      </Section>

      <Section heading={tr("4. Compartilhamento")}>
        <p>
          {tr("Não vendemos seus dados. Compartilhamos apenas com provedores essenciais ao funcionamento (por exemplo, o provedor de pagamentos) e quando exigido por lei. Pagamentos são processados por provedores externos; a AgendaPro não armazena números completos de cartão.")}
        </p>
      </Section>

      <Section heading={tr("5. Seus direitos (LGPD)")}>
        <p>
          {tr("Você pode solicitar confirmação de tratamento, acesso, correção, portabilidade, anonimização, bloqueio ou")}{' '}
          {tr("eliminação de dados, além de revogar consentimento. Para exercer esses direitos, fale com a gente pelos canais abaixo.")}
        </p>
      </Section>

      <Section heading={tr("6. Como solicitar a exclusão dos seus dados")}>
        <p>
          {tr("Envie um e-mail para")} <a href={`mailto:${supportEmail}?subject=${encodeURIComponent(tr('Solicitação LGPD - exclusão de dados'))}`} className="underline">{supportEmail}</a>{' '}
          {tr("informando quem você é e o que deseja (acesso, correção ou exclusão). Vamos autenticar sua identidade e responder dentro do prazo legal. Dados necessários para cumprimento de obrigação legal ou contratual podem ser conservados de forma isolada.")}
        </p>
      </Section>

      <Section heading={tr("7. Segurança e retenção")}>
        <p>
          {tr("Adotamos medidas técnicas e organizacionais razoáveis (acesso restrito, credenciais de servidor protegidas, sessões seguras). Guardamos os dados enquanto a conta estiver ativa ou enquanto houver obrigação legal, e depois disso os eliminamos ou anonimizamos.")}
        </p>
      </Section>

      <Section heading={tr("8. Cookies")}>
        <p>
          {tr("A AgendaPro usa um cookie de sessão (HttpOnly) para manter você conectado e armazena no")} <em>{tr("localStorage")}</em> {tr("do navegador o identificador do estabelecimento ativo para facilitar a troca de conta. Detalhes na")}{' '}
          <Link to="/cookies" className="underline">{tr("Política de cookies")}</Link>.
        </p>
      </Section>

      <Section heading={tr("9. Contato do encarregado")}>
        <p>{tr("Para assuntos de privacidade e LGPD:")} {supportEmail}.</p>
      </Section>
    </Page>
  );
}

export function CancellationPage(): JSX.Element {
  const { supportEmail } = useSiteContact();
  return (
    <Page title={tr("Política de cancelamento")} description={tr("Regras de cancelamento de agendamentos e de assinatura na AgendaPro.")} path="/cancelamento">
      <p className="text-sm text-muted">{tr('Última atualização: outubro de 2026.')}</p>

      <Section heading={tr("1. Cancelamento de agendamentos")}>
        <p>
          {tr("As condições para remarcar ou cancelar uma reserva são definidas pelo profissional/estabelecimento na política do seu perfil público e ficam visíveis antes da confirmação. Recomendamos comunicar com antecedência pelo WhatsApp ou contato informado.")}
        </p>
        <p>
          {tr("Agendamentos que exigem aprovação do profissional permanecem pendentes e podem expirar caso não sejam aprovados dentro do prazo de proteção do horário.")}
        </p>
      </Section>

      <Section heading={tr("2. Cancelamento da assinatura")}>
        <p>
          {tr("Você pode cancelar sua assinatura a qualquer momento pelo painel. Por padrão, o cancelamento é efetivado ao fim do período já pago; você mantém o acesso até o término do ciclo vigente e não há nova cobrança após isso.")}
        </p>
      </Section>

      <Section heading={tr("3. Teste gratuito")}>
        <p>
          {tr("Você pode interromper o uso durante os 7 dias de teste sem qualquer custo. Como não exigimos cartão no início, não há cobrança a cancelar durante o teste.")}
        </p>
      </Section>

      <Section heading={tr("4. Reembolsos")}>
        <p>
          {tr("A política de reembolso segue o Código de Defesa do Consumidor e as condições apresentadas na contratação. Para solicitar análise de reembolso, contate")} {supportEmail} {tr("com o código da transação.")}
        </p>
      </Section>

      <Section heading={tr("5. Contas suspensas")}>
        <p>
          {tr("Contas podem ser suspensas por violação dos termos de uso ou por pendências de pagamento, conforme previsto nos")}{' '}
          <Link to="/termos" className="underline">{tr("Termos de uso")}</Link>.
        </p>
      </Section>
    </Page>
  );
}

export function CookiesPage(): JSX.Element {
  const { supportEmail } = useSiteContact();
  return (
    <Page title={tr("Política de cookies")} description={tr("Quais tecnologias de armazenamento local a AgendaPro usa e por quê.")} path="/cookies">
      <p className="text-sm text-muted">{tr('Última atualização: outubro de 2026.')}</p>

      <Section heading={tr("1. Visão geral")}>
        <p>
          {tr("A AgendaPro é transparente sobre o que armazena no seu navegador. Usamos apenas o necessário para fazer a plataforma funcionar — não usamos Google Analytics nem rastreadores de terceiros para fins de publicidade.")}
        </p>
      </Section>

      <Section heading={tr("2. O que usamos")}>
        <div className="overflow-hidden rounded-md border border-line">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-ink">
              <tr>
                <th className="p-3">{tr("Tecnologia")}</th>
                <th className="p-3">{tr("O que faz")}</th>
                <th className="p-3">{tr("Necessária")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line text-muted">
              <tr>
                <td className="p-3">{tr("Cookie de sessão (HttpOnly)")}</td>
                <td className="p-3">{tr("Mantém você autenticado e protege o acesso ao painel. Não é legível por scripts.")}</td>
                <td className="p-3">{tr("Sim")}</td>
              </tr>
              <tr>
                <td className="p-3">{tr("localStorage (conta ativa)")}</td>
                <td className="p-3">{tr("Lembra qual estabelecimento você selecionou para facilitar a troca de conta.")}</td>
                <td className="p-3">{tr("Sim")}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section heading={tr("3. Não usamos")}>
        <p>
          {tr("Cookies de publicidade, scripts de rastreamento de terceiros (incluindo Google Analytics) e armazenamento de perfil para anúncios. Se isso mudar, atualizamos esta página e pedimos consentimento quando exigido por lei.")}
        </p>
      </Section>

      <Section heading={tr("4. Como controlar")}>
        <p>
          {tr("Você pode limpar cookies e dados de site nas configurações do navegador. Desativar o cookie de sessão impede o login e o acesso ao painel, pois ele é essencial ao funcionamento.")}
        </p>
      </Section>

      <Section heading={tr("5. Dúvidas")}>
        <p>{tr("Fale com a gente em")} {supportEmail} {tr("ou na")} <Link to="/privacidade" className="underline">{tr("Política de privacidade")}</Link>.</p>
      </Section>
    </Page>
  );
}
